import React, { useState } from 'react';

const GAIT_PARAMS = [
  { id: 'stance',   label: 'Stance Phase',   options: ['Normal','Prolonged','Shortened','Double stance ↑','Antalgic','Absent'] },
  { id: 'swing',    label: 'Swing Phase',    options: ['Normal','Reduced','Dragging','High stepping','Circumduction','Scissoring'] },
  { id: 'step_len', label: 'Step Length',    options: ['Symmetric','Asymmetric R < L','Asymmetric L < R','Reduced bilaterally','Irregular'] },
  { id: 'stride',   label: 'Stride Length',  options: ['Normal','Reduced','Irregular','Festinating (↓ festination)'] },
  { id: 'base',     label: 'Base Width',     options: ['Normal','Narrow','Wide','Variable'] },
  { id: 'cadence',  label: 'Cadence',        options: ['Normal','Slow','Fast','Irregular','Festinating'] },
];

const GAIT_PATTERNS = ['Normal','Hemiplegic (circumduction)','Ataxic (wide-based)','Steppage (high-stepping)','Waddling','Antalgic','Scissoring','Parkinsonian (shuffling)','Trendelenburg','Stamping','Foot drop'];
const ASSISTIVE_DEVICES = ['None','Walking stick (R)','Walking stick (L)','Tripod','Quadripod','Zimmer frame','Rollator','Axillary crutches','Elbow crutches','AFO (R)','AFO (L)','KAFO','Wheelchair','Orthosis'];

const GaitAnalysisAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const get = (key) => value[key] || null;
  const set = (key, val) => onChange?.({ ...value, [key]: value[key] === val ? null : val });
  const toggle = (key, f) => {
    const curr = value[key] || [];
    onChange?.({ ...value, [key]: curr.includes(f) ? curr.filter(x => x !== f) : [...curr, f] });
  };

  const assessedParams = GAIT_PARAMS.filter(p => get(`gait_${p.id}`)).length;
  const abnormalParams = GAIT_PARAMS.filter(p => { const v = get(`gait_${p.id}`); return v && v !== 'Normal' && v !== 'Symmetric'; }).length;
  const gaitPattern = value.gait_pattern || '';
  const device = value.gait_device || [];

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#713f12',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + Gait Analysis
          {assessedParams > 0 && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#713f12', color: '#fff', fontWeight: 800 }}>{assessedParams}/6 params</span>}
          {gaitPattern && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: gaitPattern === 'Normal' ? '#059669' : '#dc2626', color: '#fff', fontWeight: 800 }}>{gaitPattern}</span>}
          {abnormalParams > 0 && <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#ea580c', color: '#fff', fontWeight: 800 }}>{abnormalParams} abnormal</span>}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#713f12' }}>🚶 Gait Analysis</span>
            <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }} onMouseEnter={e => e.target.style.color = '#ef4444'} onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Gait Pattern + Assistive Device row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '12px 14px', borderRadius: '12px', background: '#fff', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Overall Gait Pattern</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {GAIT_PATTERNS.map(p => {
                  const active = gaitPattern === p;
                  const isNormal = p === 'Normal';
                  return (
                    <button key={p} type="button" onClick={() => onChange?.({ ...value, gait_pattern: active ? null : p })} style={{
                      fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                      border: `1px solid ${active ? (isNormal ? '#6ee7b7' : '#fca5a5') : '#e2e8f0'}`,
                      background: active ? (isNormal ? '#ecfdf5' : '#fef2f2') : 'transparent',
                      color: active ? (isNormal ? '#059669' : '#dc2626') : '#64748b'
                    }}>{active ? '✓ ' : ''}{p}</button>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: '12px 14px', borderRadius: '12px', background: '#fff', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assistive Device</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {ASSISTIVE_DEVICES.map(d => {
                  const active = device.includes(d);
                  const isNone = d === 'None';
                  return (
                    <button key={d} type="button" onClick={() => toggle('gait_device', d)} style={{
                      fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                      border: `1px solid ${active ? (isNone ? '#6ee7b7' : '#fcd34d') : '#e2e8f0'}`,
                      background: active ? (isNone ? '#ecfdf5' : '#fef9c3') : 'transparent',
                      color: active ? (isNone ? '#059669' : '#713f12') : '#64748b'
                    }}>{active ? '✓ ' : ''}{d}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Parameters table */}
          <div style={{ overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #fde68a', background: '#fff', minWidth: '480px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px', background: '#fefce8', padding: '7px 14px', borderBottom: '0.5px solid #fde68a' }}>
              {['Parameter','Findings','RT Finding','LT Finding'].map(h => (
                <div key={h} style={{ fontSize: '10px', fontWeight: 800, color: '#713f12', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
              ))}
            </div>
            {GAIT_PARAMS.map((p, idx) => {
              const selected = get(`gait_${p.id}`);
              const isAbnormal = selected && selected !== 'Normal' && selected !== 'Symmetric';
              return (
                <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 120px', alignItems: 'start', padding: '9px 14px', gap: '8px', borderBottom: idx < GAIT_PARAMS.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isAbnormal ? '#fffef0' : '#fff' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>{p.label}</div>
                    {isAbnormal && <div style={{ fontSize: '10px', color: '#d97706', fontWeight: 700 }}>Abnormal</div>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {p.options.map(opt => {
                      const active = selected === opt;
                      const isNorm = opt === 'Normal' || opt === 'Symmetric';
                      return (
                        <button key={opt} type="button" onClick={() => set(`gait_${p.id}`, opt)} style={{
                          fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.15s',
                          border: `1px solid ${active ? (isNorm ? '#6ee7b7' : '#fcd34d') : '#e2e8f0'}`,
                          background: active ? (isNorm ? '#ecfdf5' : '#fefce8') : 'transparent',
                          color: active ? (isNorm ? '#059669' : '#713f12') : '#64748b'
                        }}>{active ? '✓ ' : ''}{opt}</button>
                      );
                    })}
                  </div>
                  {/* RT / LT observation note */}
                  {['rt','lt'].map(side => (
                    <input key={side} type="text" placeholder={`${side.toUpperCase()} note...`}
                      value={value[`gait_${p.id}_${side}`] || ''}
                      onChange={e => onChange?.({ ...value, [`gait_${p.id}_${side}`]: e.target.value })}
                      style={{ fontSize: '10px', padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#374151', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                    />
                  ))}
                </div>
              );
            })}
          </div>

          </div>

          {/* Measurements row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginTop: '12px' }}>
            {[
              { key: 'meas_stance', label: 'Stance Phase %' },
              { key: 'meas_base',   label: 'Base Width (cm)' },
              { key: 'meas_cadence',label: 'Cadence (steps/min)' },
              { key: 'meas_speed',  label: 'Gait Speed (m/s)' },
            ].map(m => (
              <div key={m.key} style={{ padding: '10px 12px', borderRadius: '10px', background: '#fff', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#92400e', marginBottom: '5px' }}>{m.label}</div>
                <input type="text" placeholder="e.g. 60%" value={value[m.key] || ''}
                  onChange={e => onChange?.({ ...value, [m.key]: e.target.value })}
                  style={{ width: '100%', fontSize: '12px', fontWeight: 700, padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#374151', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>

          {/* Other notes */}
          <div style={{ marginTop: '10px' }}>
            <textarea rows={2} placeholder="Other gait observations..."
              value={value.gait_notes || ''}
              onChange={e => onChange?.({ ...value, gait_notes: e.target.value })}
              style={{ width: '100%', fontSize: '12px', padding: '8px 10px', border: '1px solid #fde68a', borderRadius: '8px', color: '#374151', outline: 'none', resize: 'vertical', boxSizing: 'border-box', background: '#fff' }}
            />
          </div>

          {Object.keys(value).length > 0 && <button type="button" onClick={() => onChange({})} style={{ marginTop: '8px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Clear all gait data</button>}
        </div>
      )}
    </div>
  );
};

export default GaitAnalysisAssessment;
